import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as ReactRouterLink } from "react-router-dom";

import { Alert, AlertIcon, AlertTitle, Box, Button, Link, Stack, Text } from "@chakra-ui/react";
import moment from "moment";

import { formatAmount } from "../../utils/financialStatus";

const PREVIEW_COUNT = 4;

function formatWarning(warning, months, t) {
	switch (warning.code) {
		case "missing_exchange":
			return t("financialStatus.warningMissingExchange", {
				type: warning.paymentType || "payment",
				address: warning.address || "-",
			});
		case "invalid_payment_date":
			return t("financialStatus.warningInvalidPaymentDate", {
				type: warning.paymentType || "payment",
				address: warning.address || "-",
				date: warning.paymentDate,
			});
		case "invalid_amount":
			return t("financialStatus.warningInvalidAmount", {
				type: warning.paymentType || "payment",
				address: warning.address || "-",
			});
		case "missing_owner_currency":
			return t("financialStatus.warningMissingOwnerCurrency");
		case "finalized_differs":
			return t("financialStatus.warningFinalizedDiffers", {
				month: months[warning.monthIndex] || warning.monthIndex,
				year: warning.year,
				savedAmount: formatAmount(warning.savedAmount),
				liveAmount: formatAmount(warning.liveAmount),
				currency: warning.currency,
			});
		default:
			return warning.code;
	}
}

function OwnerLabel({ warning }) {
	if (!warning.ownerName && !warning.ownerRouteId) return null;
	const label = warning.ownerName || warning.ownerRouteId;

	return (
		<>
			{warning.ownerRouteId ? (
				<Link as={ReactRouterLink} to={`/owner/view/${warning.ownerRouteId}`} fontWeight="semibold">
					{label}
				</Link>
			) : (
				<Text as="span" fontWeight="semibold">
					{label}
				</Text>
			)}
			{" — "}
		</>
	);
}

export default function FinancialStatusWarnings({ warnings = [] }) {
	const { t } = useTranslation();
	const [showAll, setShowAll] = useState(false);
	const months = moment.months();

	if (warnings.length === 0) return null;

	const visible = showAll ? warnings : warnings.slice(0, PREVIEW_COUNT);
	const hiddenCount = warnings.length - visible.length;

	return (
		<Alert status="warning" alignItems="flex-start" borderRadius="md">
			<AlertIcon />
			<Box>
				<AlertTitle>
					{t("financialStatus.warningsTitle")} ({warnings.length})
				</AlertTitle>
				<Stack spacing={1} mt={2}>
					{visible.map((warning, index) => (
						<Text
							key={`${warning.ownerId || ""}-${warning.code}-${warning.paymentId || warning.monthIndex}-${index}`}
							fontSize="sm"
						>
							<OwnerLabel warning={warning} />
							{formatWarning(warning, months, t)}
						</Text>
					))}
				</Stack>
				{(hiddenCount > 0 || showAll) && (
					<Button size="xs" variant="link" mt={2} onClick={() => setShowAll(!showAll)}>
						{showAll ? "Show less" : `Show ${hiddenCount} more`}
					</Button>
				)}
			</Box>
		</Alert>
	);
}
